import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Eye, Flame, ThumbsUp, MousePointerClick } from "lucide-react";
import { RankBadge, ScoreDonut, ClaimBadge, VerifiedBadge, LogoAvatar, ProAlumniBadge, Stars } from "@/components/SignalBadges";
import { api } from "@/lib/api";

// Video slot is admin-controlled: Verify a listing to enable its leaderboard video screen
// (any rank). Un-verifying disables it again. Logo/GIF keeps the legacy Top 3 perk.
const LOGO_SLOT_TOP_N = 3;

const mediaFrameCls =
  "order-last mt-3 w-full basis-full md:order-none md:mt-0 md:w-36 md:basis-auto lg:w-44 aspect-video shrink-0 rounded-xl border border-emerald-400/30 bg-black/60 p-1 shadow-[0_10px_28px_rgba(0,0,0,0.28),0_0_22px_rgba(63,224,140,0.10)]";
const mediaInnerCls = "w-full h-full rounded-lg overflow-hidden bg-[#070706]";

export default function LeaderboardRow({ company, index = 0, showWeekly = false }) {
  const showVideoSlot = Boolean(company.verified);
  const showLogoSlot = company.media_rank_position <= LOGO_SLOT_TOP_N && (company.gif_url || company.logo_url);
  const isTop3 = Boolean(company.top3_position) || company.rank_position <= 3;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.35), ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/company/${company.slug}`}
        onClick={() => { api.post(`/companies/${company.slug}/click`).catch(() => {}); }}
        data-testid={`leaderboard-row-${company.slug}`}
        className={`group relative flex flex-wrap items-center gap-3 sm:gap-4 px-3 sm:px-5 py-3.5 sm:py-4 rounded-xl border transition-all duration-300 hover:-translate-y-0.5 overflow-hidden ${
          isTop3
            ? "top-spot-row hover:shadow-[0_18px_45px_rgba(0,0,0,0.34),0_0_34px_rgba(63,224,140,0.18)]"
            : "border-white/[0.07] bg-[#0A0A09] hover:bg-[#121210] hover:border-emerald-500/30 hover:shadow-[0_0_30px_rgba(16,185,129,0.08)]"
        }`}
      >
        <div className="shrink-0">
          <RankBadge rank={company.top_spot_position || company.rank_position} boosted={company.boosted} testId={`rank-badge-${company.slug}`} />
        </div>
        <LogoAvatar name={company.name} logoUrl={company.logo_url} testId={`avatar-${company.slug}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <ClaimBadge claimed={company.claimed} testId={`claim-badge-${company.slug}`} />
            {company.verified && <VerifiedBadge testId={`verified-badge-${company.slug}`} />}
            {company.pro_alumni && <ProAlumniBadge testId={`pro-alumni-badge-${company.slug}`} />}
            <span className="font-heading font-bold text-sm sm:text-lg text-slate-100 group-hover:text-emerald-300 transition-colors duration-200 truncate max-w-full">
              {company.name}
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 truncate mt-0.5">{company.tagline}</p>
          <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 flex-wrap">
            <span className="font-mono2 text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 border border-white/10 rounded px-1.5 py-0.5">
              {company.category}
            </span>
            <span className="font-mono2 text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 border border-white/10 rounded px-1.5 py-0.5">
              {company.region}
            </span>
            {company.average_rating != null && (
              <span
                data-testid={`star-rating-${company.slug}`}
                className="inline-flex items-center gap-1 border border-emerald-500/30 bg-emerald-500/10 rounded px-1.5 py-0.5"
              >
                <Stars value={company.average_rating} size={9} testId={`stars-${company.slug}`} />
                <span className="font-mono2 text-[9px] sm:text-[10px] text-emerald-400 ml-0.5">
                  {company.average_rating.toFixed(1)}
                </span>
              </span>
            )}
            <span
              data-testid={`upvotes-${company.slug}`}
              className="font-mono2 text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1"
            >
              <ThumbsUp size={10} /> {company.upvotes || 0}
            </span>
            <span className="font-mono2 text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1">
              <Eye size={10} /> {company.views || 0}
            </span>
            <span
              data-testid={`clicks-${company.slug}`}
              title="Listing clicks — how many times people opened this listing"
              className="font-mono2 text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1"
            >
              <MousePointerClick size={10} /> {company.clicks || 0}
            </span>
            {showWeekly && (
              <span
                data-testid={`weekly-views-${company.slug}`}
                className="font-mono2 text-[9px] sm:text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
              >
                <Flame size={10} /> {company.weekly_views || 0} this week
              </span>
            )}
          </div>
        </div>
        {showVideoSlot &&
          (company.slot_video_url ? (
            <div
              data-testid={`video-slot-${company.slug}`}
              title="Top 3 video clip — unlocked through Boost"
              className={mediaFrameCls}
            >
              <div className={mediaInnerCls}>
                <video
                  ref={(el) => {
                    if (el) {
                      el.muted = true;
                      el.play().catch(() => {});
                    }
                  }}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover"
                >
                  {company.slot_video_webm && <source src={company.slot_video_webm} type="video/webm" />}
                  <source src={company.slot_video_url} type="video/mp4" />
                </video>
              </div>
            </div>
          ) : showLogoSlot ? (
            <div
              data-testid={`video-slot-${company.slug}`}
              title={
                company.gif_url
                  ? "Animated logo — Top 3 perk, unlocked through Boost"
                  : "Static logo — the animated version unlocks with an approved GIF"
              }
              className={mediaFrameCls}
            >
              <div className={mediaInnerCls}>
                <img
                  src={company.gif_url || company.logo_url}
                  alt={`${company.name} logo`}
                  loading={index < 3 ? "eager" : "lazy"}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          ) : (
            <div
              data-testid={`video-slot-${company.slug}`}
              title="Top 3 media slot — add an approved GIF or video"
              className={`${mediaFrameCls} border-emerald-400/25`}
            >
              <div className="relative w-full h-full rounded-lg overflow-hidden bg-[radial-gradient(circle_at_30%_20%,rgba(63,224,140,0.14),transparent_45%),linear-gradient(135deg,rgba(63,224,140,0.08),rgba(255,255,255,0.02))] flex flex-col items-center justify-center gap-1.5">
                <span className="w-7 h-7 rounded-full border border-emerald-400/30 bg-emerald-400/10 flex items-center justify-center shadow-[0_0_16px_rgba(63,224,140,0.16)]">
                  <Play size={12} className="text-emerald-300 fill-emerald-300/20" />
                </span>
                <span className="font-mono2 text-[8px] uppercase tracking-[0.18em] text-emerald-300/90 text-center leading-tight px-2">
                  {company.media_rank_position <= LOGO_SLOT_TOP_N ? "Media slot" : "Video slot"}
                  <br />
                  <span className="text-slate-400">
                    {company.media_rank_position <= LOGO_SLOT_TOP_N ? `Top ${LOGO_SLOT_TOP_N} · pending` : "Available"}
                  </span>
                </span>
              </div>
            </div>
          ))}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <ScoreDonut score={company.highrank_score} size={46} testId={`score-badge-${company.slug}`} />
          <span className="hidden sm:block font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-400">
            HighRank Score
          </span>
        </div>
      </Link>
    </motion.div>
  );
}
